import { z } from "zod";
import { Id } from "./common.ts";
import { adminEndpoint, type Endpoint } from "./endpoint.ts";

/** A group of endpoints (`xxxApi` objects). Area files declare `export const fooApi = { … } satisfies EndpointGroup`. */
export type EndpointGroup = Record<string, Endpoint>;

/**
 * Shell data (owned by the foundation): sidebar counters and global search.
 *
 * Sidebar badges replace the bell (removed in September, Admin-Dashboard dev zone): GET /admin/inbox-summary,
 * polled every 30 s, silent on failure. Counters the user's role cannot see come back absent, never 0.
 */
export const InboxSummary = z.object({
  /** Création d'entreprise: new requests to take (BO-Creation-Nouvelles tab count). */
  creationNew: z.number().int().optional(),
  /** Création: recent activity in the last 24 h (indigo dot on the inactive space pill). */
  creationActivity: z.boolean().optional(),
  /** Compta: accounting onboardings waiting on Melrys (Clients badge, « {n} en attente » on the Compta pill). */
  onboardingsToDo: z.number().int().optional(),
  /** Compta: an onboarding started or moved in the last 24 h (indigo dot on the inactive Compta pill). */
  comptaActivity: z.boolean().optional(),
  /** Compta: declarations to establish (Impôts et taxes badge). */
  taxesToDo: z.number().int().optional(),
  /** Rendez-vous: app visios still waiting for a Google Meet link pasted by the team (Rendez-vous badge). */
  meetLinksToSet: z.number().int().optional(),
  /** Unread client messages (Discussions / Messagerie badge). */
  discussionsUnread: z.number().int().optional(),
  /** Finance: payments to confirm, refunds to issue. */
  financePayments: z.number().int().optional(),
  financeRefunds: z.number().int().optional(),
  /** Facturation SaaS: e-invoicing flows in error, open incidents. */
  saasFlows: z.number().int().optional(),
  saasIncidents: z.number().int().optional(),
});
export type InboxSummary = z.infer<typeof InboxSummary>;

export const SearchHit = z.object({
  kind: z.enum(["company", "creation_dossier", "declaration", "staff"]),
  id: Id,
  label: z.string(),
  sublabel: z.string().nullable(),
  /** App route to open (the admin router path). */
  href: z.string(),
});
export type SearchHit = z.infer<typeof SearchHit>;

export const shellApi = {
  inboxSummary: adminEndpoint({ id: "admin.shell.inboxSummary", method: "GET", path: "/admin/inbox-summary", response: InboxSummary, permission: ["settings.personal", "read"] }),
  /** Sidebar search « Rechercher un client, une déclaration… » (no behaviour drawn: results menu under the field). */
  search: adminEndpoint({
    id: "admin.shell.search",
    permission: ["settings.personal", "read"],
    method: "GET",
    path: "/admin/search",
    query: z.object({ q: z.string().trim().min(2) }),
    response: z.object({ hits: z.array(SearchHit) }),
  }),
} satisfies EndpointGroup;
